import '../styles/TransferInstrumentPanel.css';
import React, { useMemo, useState } from 'react';
import { useProjectCollection, projectData } from '../lib/projectData';

const CURRENCY = 'USD';
const DIAL_MIN_ANGLE = -120;
const DIAL_MAX_ANGLE = 120;
const DIAL_CEILING = 5000;

const TICKS = Array.from({ length: 25 }, (_, i) => i);

function formatAmount(value) {
  const numeric = Number(value) || 0;
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function angleForAmount(value) {
  const numeric = Number(value) || 0;
  const ratio = Math.max(0, Math.min(1, numeric / DIAL_CEILING));
  return DIAL_MIN_ANGLE + ratio * (DIAL_MAX_ANGLE - DIAL_MIN_ANGLE);
}

function nextLedgerId(dateStr, count) {
  const compact = dateStr.replace(/-/g, '');
  const seq = String((count + 1) % 100).padStart(2, '0');
  return `txn-${compact}-${seq}`;
}

function isoDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function TransferInstrumentPanel({ onSubmit, onTransferComplete }) {
  const accounts = useProjectCollection('client_accounts');
  const ledger = useProjectCollection('ledger_entries');

  const [recipientName, setRecipientName] = useState('');
  const [recipientAccount, setRecipientAccount] = useState('');
  const [reference, setReference] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [completionRecord, setCompletionRecord] = useState(null);

  const parsedAmount = Number.isFinite(Number(amountInput)) && amountInput !== ''
    ? Number(amountInput)
    : 0;

  const needleAngle = useMemo(() => angleForAmount(parsedAmount), [parsedAmount]);

  const accountList = Array.isArray(accounts.data) ? accounts.data : [];
  const availableAccount = accountList.length > 0 ? accountList[0] : null;

  const resetForm = () => {
    setRecipientName('');
    setRecipientAccount('');
    setReference('');
    setAmountInput('');
    setFieldErrors({});
    setSubmitError('');
    setSubmitStatus('idle');
  };

  const validate = () => {
    const errors = {};
    if (!recipientName.trim()) errors.recipientName = 'Enter the recipient name.';
    if (!recipientAccount.trim()) {
      errors.recipientAccount = 'Enter the recipient account number.';
    }
    if (!amountInput.trim() || !(parsedAmount > 0)) {
      errors.amount = 'Enter an amount greater than zero.';
    }
    return errors;
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    setSubmitError('');

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitStatus('idle');
      return;
    }

    setSubmitStatus('submitting');

    const dateStr = isoDate(0);
    const amount = Number(parsedAmount.toFixed(2));
    const description = `Transfer to ${recipientName.trim()}`;

    try {
      // Validate the external submission boundary before recording a successful debit.
      let result = null;
      if (typeof onSubmit === 'function') {
        result = await onSubmit({
          recipientName: recipientName.trim(),
          recipientAccount: recipientAccount.trim(),
          reference: reference.trim(),
          amount,
          currency: availableAccount && availableAccount.currency
            ? availableAccount.currency
            : CURRENCY,
          date: dateStr,
        });
      }

      if (result && result.ok === false) {
        setSubmitError(result.error || 'The transfer could not be submitted.');
        setSubmitStatus('error');
        return;
      }

      const record = {
        id: nextLedgerId(dateStr, Array.isArray(ledger.data) ? ledger.data.length : 0),
        date: dateStr,
        description,
        counterparty: recipientName.trim(),
        amount,
        direction: 'debit',
        status: 'settled',
      };
      await projectData.create('ledger_entries', record);
      if (typeof onTransferComplete === 'function') onTransferComplete();

      setCompletionRecord({
        recipientName: recipientName.trim(),
        recipientAccount: recipientAccount.trim(),
        reference: reference.trim(),
        amount,
        date: dateStr,
        status: 'settled',
      });
      setSubmitStatus('complete');
      if (typeof ledger.refresh === 'function') ledger.refresh();
    } catch (err) {
      setSubmitError(
        err && err.message ? err.message : 'The transfer could not be submitted.'
      );
      setSubmitStatus('error');
    }
  };

  const handleEdit = () => {
    setCompletionRecord(null);
    resetForm();
  };

  const isSubmitting = submitStatus === 'submitting';

  return (
    <section className="tip-root" aria-labelledby="tip-title">
      <div className="tip-inner">
        <div className="tip-head">
          <p className="tip-micro">Fund transfer</p>
          <h2 className="tip-title" id="tip-title">
            Initiate transfer
          </h2>
          <p className="tip-sub">
            Enter the recipient details and set the amount. The dial reads the amount as
            you type, so the number is measured before it is confirmed.
          </p>
        </div>

        <div className="tip-panel">
          <div className="tip-panel-head">
            <span className="tip-panel-label">
              Source account: {availableAccount
                ? `${availableAccount.holderName || availableAccount.email || availableAccount.id} · ${availableAccount.id}`
                : 'Unavailable'}
            </span>
            <span className="tip-panel-value">
              {availableAccount && availableAccount.currency
                ? availableAccount.currency
                : CURRENCY}
            </span>
          </div>

          {submitStatus === 'complete' && completionRecord ? (
            <div className="tip-completion">
              <div className="tip-completion-head">
                <span className="tip-completion-mark">
                  <span className="tip-completion-dot" aria-hidden="true" />
                  Transfer recorded
                </span>
              </div>
              <p className="tip-completion-line">
                The transfer to {completionRecord.recipientName} has been recorded and
                now appears in the ledger.
              </p>

              <div className="tip-ledger">
                <div className="tip-row">
                  <span className="tip-row-label">Recipient</span>
                  <span className="tip-row-value">{completionRecord.recipientName}</span>
                </div>
                <div className="tip-row">
                  <span className="tip-row-label">Account</span>
                  <span className="tip-row-value">
                    {completionRecord.recipientAccount}
                  </span>
                </div>
                <div className="tip-row">
                  <span className="tip-row-label">Reference</span>
                  <span className="tip-row-value">
                    {completionRecord.reference || 'No reference supplied'}
                  </span>
                </div>
                <div className="tip-row">
                  <span className="tip-row-label">Amount</span>
                  <span className="tip-row-value is-debit">
                    −
                    {formatAmount(completionRecord.amount)} {CURRENCY}
                  </span>
                </div>
                <div className="tip-row">
                  <span className="tip-row-label">Date</span>
                  <span className="tip-row-value">{completionRecord.date}</span>
                </div>
                <div className="tip-row">
                  <span className="tip-row-label">Status</span>
                  <span className="tip-row-value">{completionRecord.status}</span>
                </div>
              </div>

              <button
                type="button"
                className="tip-secondary-action"
                onClick={handleEdit}
              >
                Start another transfer
              </button>
            </div>
          ) : (
            <form className="tip-panel-body" onSubmit={handleConfirm} noValidate>
              <div className="tip-col-fields">
                <div className="tip-field">
                  <label className="tip-field-label" htmlFor="tip-recipient-name">
                    Recipient name
                  </label>
                  <input
                    id="tip-recipient-name"
                    className={
                      fieldErrors.recipientName ? 'tip-input is-invalid' : 'tip-input'
                    }
                    type="text"
                    name="recipientName"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Account holder name"
                    autoComplete="off"
                    aria-invalid={fieldErrors.recipientName ? 'true' : 'false'}
                    aria-describedby={
                      fieldErrors.recipientName ? 'tip-recipient-name-error' : undefined
                    }
                    disabled={isSubmitting}
                  />
                  {fieldErrors.recipientName ? (
                    <p className="tip-error" id="tip-recipient-name-error">
                      {fieldErrors.recipientName}
                    </p>
                  ) : null}
                </div>

                <div className="tip-field">
                  <label className="tip-field-label" htmlFor="tip-recipient-account">
                    Recipient account
                  </label>
                  <input
                    id="tip-recipient-account"
                    className={
                      fieldErrors.recipientAccount ? 'tip-input is-invalid' : 'tip-input'
                    }
                    type="text"
                    name="recipientAccount"
                    value={recipientAccount}
                    onChange={(e) => setRecipientAccount(e.target.value)}
                    placeholder="Account number"
                    autoComplete="off"
                    aria-invalid={fieldErrors.recipientAccount ? 'true' : 'false'}
                    aria-describedby={
                      fieldErrors.recipientAccount
                        ? 'tip-recipient-account-error'
                        : undefined
                    }
                    disabled={isSubmitting}
                  />
                  {fieldErrors.recipientAccount ? (
                    <p className="tip-error" id="tip-recipient-account-error">
                      {fieldErrors.recipientAccount}
                    </p>
                  ) : null}
                </div>

                <div className="tip-field">
                  <label className="tip-field-label" htmlFor="tip-reference">
                    Reference
                  </label>
                  <input
                    id="tip-reference"
                    className="tip-input"
                    type="text"
                    name="reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Optional payment reference"
                    autoComplete="off"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="tip-col-dial">
                <div className="tip-field">
                  <label className="tip-field-label" htmlFor="tip-amount">
                    Amount
                  </label>
                  <input
                    id="tip-amount"
                    className={fieldErrors.amount ? 'tip-input is-invalid' : 'tip-input'}
                    type="number"
                    name="amount"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    placeholder="0.00"
                    aria-invalid={fieldErrors.amount ? 'true' : 'false'}
                    aria-describedby={fieldErrors.amount ? 'tip-amount-error' : undefined}
                    disabled={isSubmitting}
                  />
                  {fieldErrors.amount ? (
                    <p className="tip-error" id="tip-amount-error">
                      {fieldErrors.amount}
                    </p>
                  ) : null}
                </div>

                <div className="tip-dial-frame">
                  <svg
                    className="tip-dial-svg"
                    viewBox="0 0 240 240"
                    role="img"
                    aria-label={`Amount dial reading ${formatAmount(parsedAmount)} ${CURRENCY}`}
                  >
                    <circle
                      className="tip-dial-track-outer"
                      cx="120"
                      cy="120"
                      r="112"
                    />
                    <circle className="tip-dial-track" cx="120" cy="120" r="104" />
                    <path
                      className="tip-dial-arc"
                      d="M 37.4 193.9 A 104 104 0 0 1 202.6 193.9"
                    />
                    {TICKS.map((i) => {
                      const major = i % 6 === 0;
                      const angle = -120 + (i / 24) * 240;
                      const inner = major ? 78 : 88;
                      return (
                        <line
                          key={i}
                          className={major ? 'tip-tick is-major' : 'tip-tick'}
                          x1="120"
                          y1={120 - inner}
                          x2="120"
                          y2="30"
                          transform={`rotate(${angle} 120 120)`}
                        />
                      );
                    })}
                    <g
                      className="tip-dial-needle"
                      style={{ transform: `rotate(${needleAngle}deg)` }}
                    >
                      <line
                        className="tip-dial-needle-line"
                        x1="120"
                        y1="120"
                        x2="120"
                        y2="42"
                      />
                      <circle className="tip-dial-hub" cx="120" cy="120" r="5" />
                    </g>
                    <circle className="tip-dial-hub-ring" cx="120" cy="120" r="10" />
                  </svg>
                </div>

                <div className="tip-readout">
                  <p
                    className={parsedAmount > 0 ? 'tip-amount' : 'tip-amount is-empty'}
                    aria-live="polite"
                  >
                    {formatAmount(parsedAmount)}
                  </p>
                  <span className="tip-amount-caption">{CURRENCY} · transfer amount</span>
                </div>
              </div>

              <div className="tip-confirm-zone">
                {submitStatus === 'error' && submitError ? (
                  <p className="tip-submit-error" role="alert">
                    {submitError}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="tip-confirm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="tip-inprogress">
                      <span className="tip-inprogress-sweep" aria-hidden="true" />
                      Submitting transfer
                    </span>
                  ) : (
                    'Confirm transfer'
                  )}
                </button>

                <p className="tip-note">
                  Confirm to record the transfer. Entered values are preserved if the
                  submission does not complete.
                </p>
              </div>
            </form>
          )}

          {accounts.error || ledger.error ? (
            <p className="tip-note" role="status">
              Account and ledger data are temporarily unavailable; values you enter are
              not affected.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default TransferInstrumentPanel;